import { useQuery } from "@tanstack/react-query";
import {
  fetchAllClutchScores,
  fetchLiveGames,
  fetchLiveGameSummary,
  fetchTeamData,
} from "../services/LiveGameService";
import { ClutchBreakdown } from "../services/ClutchService";
import { useLeague } from "../context/LeagueContext";
import {
  FaCircle,
  FaDollarSign,
} from "react-icons/fa";
import { Box, CircularProgress, Typography } from "@mui/material";
import "./AllGames.css";
import GamePopoverCard from "../components/GamePopoverCard";
import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { GiWhistle } from "react-icons/gi";
import { FaCloudBolt } from "react-icons/fa6";
import { LEAGUE_OPTIONS } from "../const/leagues";
import { isOffseason } from "../utils/offseason";
import { IoCloudOfflineOutline } from "react-icons/io5";
import { getFriendlyDay } from "../utils/friendlyDay";
import { UnderdogHypeBottomSheet } from "../components/WinCounter";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";

function findTeamMetaByEspnId(meta: Record<string, any> | undefined, espnId: string) {
  if (!meta) return undefined;
  return Object.values(meta).find((team: any) => team.espnTeamId === espnId);
}

export default function AllGames() {
  const { league } = useLeague();
  const [summaryMap, setSummaryMap] = useState<Map<string, any>>(new Map());
  const [expandedGameId, setExpandedGameId] = useState<string | null>(null);
  const [showContent, setShowContent] = useState(false);
  const isOffseasonNow = useMemo(() => isOffseason(league), [league]);

  const [dayFilter, setDayFilter] = useState<
    "yesterday" | "today" | "upcoming"
  >("today");

  const { data: gamesData, isLoading } = useQuery({
    queryKey: ['schedule', league, dayFilter], 
    queryFn: () => fetchLiveGames(league, dayFilter),
    refetchInterval: dayFilter === 'today' ? 30_000 : false,
  });
  

  const { data: teamMeta } = useQuery({
    queryKey: ["teamMeta", league],
    queryFn: () => fetchTeamData(league),
    staleTime: 86400000,
  });

  
  const { data: clutchScores } = useQuery({
    queryKey: ["clutchScores", league],
    queryFn: () => fetchAllClutchScores(league),
  });
  
  
  const clutchMap = useMemo(() => {
    const map = new Map<string, ClutchBreakdown>();
  
    if (clutchScores?.results?.length) {
      clutchScores.results.forEach((team: any) => {
        map.set(team.id, {
          teamId: team.id,
          score: team.clutch,
          breakdown: team.breakdown,
        });
      });
    }
  
    return map;
  }, [clutchScores]);

  useEffect(() => {
    if (!expandedGameId) return;
  
    const interval = setInterval(async () => {
      const freshData = await fetchLiveGames(league);
      if (freshData) {
        setSummaryMap(prev => new Map(prev).set(expandedGameId, freshData));
      }
    }, 15000);
  
    return () => clearInterval(interval);
  }, [expandedGameId, league]);

  useEffect(() => {
    logEvent(analytics, 'view_all_games');
  }, []);

  const handleExpandGame = (gameId: string, eventId: string) => {
    setExpandedGameId((prev) => {
      const isSameGame = prev === gameId;
      setShowContent(!isSameGame);
      return isSameGame ? null : gameId;
    });
  
    if (!summaryMap.has(gameId)) {
      fetchLiveGameSummary(league, eventId).then((summary) => {
        setSummaryMap((prev) => new Map(prev).set(gameId, summary));
      });
    }
  };
  
  
  
  if (isLoading) {
    return (
      <Box
        p={4}
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
        className="loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!Array.isArray(gamesData)) return null;

  const uniqueGames = Array.from(
    new Map(gamesData.map((game) => [game.eventId, game])).values()
  );

  const getStatusWeight = (status: string) => {
    if (status.toLowerCase() === "live") return 0;
    if (status.toLowerCase() === "final") return 1;
    if (status.toLowerCase() === "postponed") return 99;
    return 2; // other statuses (upcoming etc.)
  };
  

  const sortedGames = [...uniqueGames].sort((a, b) => {
    // 1) If today, force any "in progress"/live games to the top:
    if (dayFilter === 'today') {
      const isLive = (g: any) =>
        g.status.toLowerCase().includes('in progress') ||
        g.status.toLowerCase().includes('live');
      const aLive = isLive(a);
      const bLive = isLive(b);
      if (aLive && !bLive) return -1;
      if (bLive && !aLive) return 1;
    }
  
    // 2) Your existing weight-based sort:
    const weightDiff = getStatusWeight(a.status) - getStatusWeight(b.status);
    if (weightDiff !== 0) return weightDiff;
  
    // 3) Then time-of-day for any ties:
    const timeA = new Date(`${a.date}T${a.time}`).getTime();
    const timeB = new Date(`${b.date}T${b.time}`).getTime();
    return timeA - timeB;
  });
  

  const upperLeague = league.toUpperCase();

  if (isOffseasonNow) {
    return (
      <Box p={4} textAlign="center">
        <IoCloudOfflineOutline size={120} style={{ marginBottom: "16px" }} />
        <Typography variant="h6">
          {LEAGUE_OPTIONS[league]?.label ?? league.toUpperCase()} is currently
          in offseason.
        </Typography>
        <Typography variant="body2" color="textSecondary">
          HypeScores are still available. Games will appear once the new season
          begins.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      p={2}
      display="flex"
      flexDirection="column"
      gap={1}
      maxWidth="800px"
      mx="auto"
    >
      <Box className="tab-group">
        {(['yesterday','today','upcoming'] as const).map(d => (
          <button
            key={d}
            onClick={() => setDayFilter(d)}
            className={`filter-tab ${dayFilter === d ? 'active' : ''}`}
          >
            {d.charAt(0).toUpperCase() + d.slice(1)}
          </button>
        ))}
      </Box>

      <UnderdogHypeBottomSheet dayFilter={dayFilter} />


      {sortedGames.length === 0 && (
        <div className="error-msg">
          <GiWhistle size={120} />
          <p className="sorry">
            {dayFilter === "upcoming"
              ? `No live ${upperLeague} games currently. Check back later.`
              : "Games will show up here after completion."}
          </p>
        </div>
      )}

      {sortedGames.map((game) => {
        // console.log('game', game);

        const awayMeta = findTeamMetaByEspnId(teamMeta, game.awayTeamId);
        const homeMeta = findTeamMetaByEspnId(teamMeta, game.homeTeamId);

        const awayLogo = awayMeta?.logo;
        const homeLogo = homeMeta?.logo;
        const awayColor = awayMeta?.color ?? "444"; // fallback if missing
        const homeColor = homeMeta?.color ?? "444";

        const awayTeamId = awayMeta?.teamId;
        const homeTeamId = homeMeta?.teamId;

        const awayClutch = awayTeamId
          ? clutchMap.get(awayTeamId)?.score ?? 0
          : 0;
        const homeClutch = homeTeamId
          ? clutchMap.get(homeTeamId)?.score ?? 0
          : 0;

        const awayBreakdown = awayTeamId
          ? clutchMap.get(awayTeamId)?.breakdown
          : undefined;
        const homeBreakdown = homeTeamId
          ? clutchMap.get(homeTeamId)?.breakdown
          : undefined;

        // Underdog
        const [favoriteTeamId, favoriteIsAway] = (() => {
          if (!game.line) return [null, null];

          const [favAbbr] = game.line.split(" ");
          if (!favAbbr) return [null, null];

          const favoriteAbbr = favAbbr.toLowerCase();

          const awayMetaId = awayMeta?.teamId; // ← FIXED
          const homeMetaId = homeMeta?.teamId; // ← FIXED

          if (awayMetaId === favoriteAbbr) return [awayMetaId, true];
          if (homeMetaId === favoriteAbbr) return [homeMetaId, false];

          return [null, null];
        })();

        const isHypingUnderdog = (() => {
          if (!favoriteTeamId) return false;

          if (favoriteIsAway) {
            // favorite = away, so underdog = home
            return homeClutch > awayClutch;
          } else {
            // favorite = home, so underdog = away
            return awayClutch > homeClutch;
          }
        })();

        const isHomeMoreClutch = homeClutch > awayClutch;
        const clutchGradient = `linear-gradient(to right, ${
          isHomeMoreClutch ? `#${awayColor}10` : `#${awayColor}`
        } 0%, #1b1c1f 30%, #1b1c1f 70%, ${
          isHomeMoreClutch ? `#${homeColor}` : `#${homeColor}10`
        } 100%)`;

        return (
          <div
            key={game.eventId}
            className="matchup-hover-wrapper"
            onClick={() => handleExpandGame(game.id, game.eventId)}
          >
            <motion.div
              layout
              id={`game-${game.id}`}
              onLayoutAnimationComplete={() => {
                if (expandedGameId === game.id) {
                  const el = document.getElementById(`game-${game.id}`);
                  if (el) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }
                }
              }}
              className={`matchup-tile 
                ${
                  ["postponed"].includes(game.status.toLowerCase())
                    ? "matchup-title--postponed"
                    : ""
                }
                ${
                  isHypingUnderdog && game.status.toLowerCase() === "final"
                    ? "underdog-hype-winner"
                    : isHypingUnderdog
                    ? "underdog-highlight"
                    : ""
                }
                
              `}
              style={{ background: clutchGradient }}
            >
              <div className="team-section">
                {awayLogo && (
                  <img
                    src={awayLogo}
                    alt={awayMeta?.teamName || "Away Team"}
                    className="team-logo"
                  />
                )}
              </div>

              <div className="matchup-center">
              {game.seriesInfo && <span>{game.seriesInfo}</span>}


                <div className="live-score clutch-score-display">
                  <span>{awayClutch}</span>
                  <span className="score">{game.awayTeamScore}</span>
                  <span className="score-divider">vs</span>
                  <span className="score">{game.homeTeamScore}</span>
                  <span>{homeClutch}</span>
                </div>

                {["in progress"].includes(game.status.toLowerCase()) && (
                  <div className="status">
                    <div className="meta-inning">
                      {game.liveStatus?.type?.detail}
                    </div>
                    <span className="live-badge">
                      <FaCircle className="live-dot" />
                      <span className="live-text">LIVE</span>
                    </span>
                  </div>
                )}

                {["final"].includes(game.status.toLowerCase()) && (
                  <div className="status">
                    <span className="final">FINAL</span>
                  </div>
                )}

                {(
                  ['scheduled', 'final'].includes(game.status.toLowerCase())
                ) && (
                  <div className="status">
                    <span className="matchup-time">
                      {getFriendlyDay(game.date)} – {game.time}
                    </span>
                  </div>
                )}

                {["postponed"].includes(game.status.toLowerCase()) && (
                  <div className="status">
                    <span className="postponed">
                      <FaCloudBolt /> Postponed
                    </span>
                  </div>
                )}


                <div className="betting-odds">
                  {game.line && (
                    <span className="odds-line">
                      <FaDollarSign className="odds-icon" />
                      {game.line}
                    </span>
                  )}
                </div>
              </div>

              <div className="team-section">
                {homeLogo && (
                  <img
                    src={homeLogo}
                    alt={homeMeta?.teamName || "Home Team"}
                    className="team-logo"
                  />
                )}
              </div>
            </motion.div>

            {expandedGameId === game.id && (
              <motion.div
                layout
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
                className="expanded-breakdown"
                style={{
                  background: `linear-gradient(to right, #${awayColor}20, #1b1c1f, #${homeColor}20)`,
                  borderLeft: `4px solid #${game.homeTeam.color}`,
                  padding: "16px",
                  borderRadius: "0 0 8px 8px",
                }}
              >
                {showContent && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.05, duration: 0.2 }}
                  >
                    <GamePopoverCard
                      sport={league}
                      className="inline-breakdown"
                      homeLogo={homeLogo}
                      awayLogo={awayLogo}
                      status={game.status}
                      period={game.period?.toString() ?? "-"}
                      balls={
                        typeof game.balls === "string"
                          ? parseInt(game.balls)
                          : game.balls
                      }
                      strikes={
                        typeof game.strikes === "string"
                          ? parseInt(game.strikes)
                          : game.strikes
                      }
                      outs={
                        typeof game.outs === "string"
                          ? parseInt(game.outs)
                          : game.outs
                      }
                      pitcher={game.pitcher}
                      batter={game.batter}
                      onDeck={game.onDeck}
                      winningPitcher={
                        game.winningPitcher
                          ? {
                              id: game.winningPitcher.id,
                              fullName: game.winningPitcher.fullName,
                              shortName: game.winningPitcher.shortName,
                              headshot: game.winningPitcher.headshot,
                              teamId: game.winningPitcher?.teamId,
                              position: game.winningPitcher.position,
                            }
                          : null
                      }
                      losingPitcher={
                        game.losingPitcher
                          ? {
                              id: game.losingPitcher.id,
                              fullName: game.losingPitcher.fullName,
                              shortName: game.losingPitcher.shortName,
                              headshot: game.losingPitcher.headshot,
                              teamId: game.losingPitcher?.teamId,
                              position: game.losingPitcher.position,
                            }
                          : null
                      }
                      savePitcher={game.savePitcher}
                      homeTeamName={homeMeta?.teamName ?? "Home"}
                      awayTeamName={awayMeta?.teamName ?? "Away"}
                      homeTeam={{
                        abbreviation: homeMeta?.teamId?.toUpperCase() ?? "",
                        linescores: game.homeTeam?.linescores ?? [],
                        runs: game.homeTeam?.runs ?? "-",
                        hits: game.homeTeam?.hits ?? "-",
                        errors: game.homeTeam?.errors ?? "-",
                      }}
                      awayTeam={{
                        abbreviation: awayMeta?.teamId?.toUpperCase() ?? "",
                        linescores: game.awayTeam?.linescores ?? [],
                        runs: game.awayTeam?.runs ?? "-",
                        hits: game.awayTeam?.hits ?? "-",
                        errors: game.awayTeam?.errors ?? "-",
                      }}
                      occupiedBases={game.occupiedBases}
                      homeScore={game.homeTeamScore}
                      awayScore={game.awayTeamScore}
                      moneyline={game.line}
                      network={game.network}
                      awayClutchScore={awayClutch}
                      homeClutchScore={homeClutch}
                      awayClutchBreakdown={awayBreakdown}
                      homeClutchBreakdown={homeBreakdown}
                    />
                  </motion.div>
                )}
              </motion.div>
            )}
          </div>
        );
      })}
      <div className="legend">
        <div className="legend-item">
          <span className="legend-line yellow" />
          <span className="legend-text">Hype underdog vs Moneyline favorite</span>
        </div>
        <div className="legend-item">
          <span className="legend-line green" />
          <span className="legend-text">Hype outperformed Moneyline</span>
        </div>
      </div>
    </Box>
  );
}
